<template>
  <div class="floating-tools">
    <button
      v-show="showTop"
      class="floating-tools__btn floating-tools__btn--top"
      type="button"
      @click="toTop"
      title="顶部"
    >
      <span class="floating-tools__icon">
        <i class="el-icon-position"></i>
      </span>
      <span class="floating-tools__text">顶部</span>
    </button>

    <button
      v-show="showServiceButton"
      class="floating-tools__btn"
      :class="serviceButtonClass"
      type="button"
      @click="openService"
      :title="serviceTitle"
    >
      <span class="floating-tools__icon">
        <template v-if="serviceKind === 'work_wecom'">
          <span class="floating-tools__icon-text">客</span>
        </template>
        <template v-else>
          <i class="el-icon-chat-dot-round"></i>
        </template>
      </span>
      <span class="floating-tools__text">{{ serviceText }}</span>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import chatApi from "@/api/chat";

export default {
  name: "FloatingTools",
  props: {
    kfUrl: {
      type: String,
      default: ""
    },
    kfEnabled: {
      type: Boolean,
      default: true
    },
    topVisibilityHeight: {
      type: Number,
      default: 200
    },
    chatbotEnabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showTop: false,
      opening: false
    };
  },
  computed: {
    ...mapGetters(["chatInfo"]),
    resolvedProvider() {
      return (this.chatInfo && this.chatInfo.provider) || "";
    },
    serviceKind() {
      // work_wecom / wechat_chatbot / ''
      return this.resolvedProvider;
    },
    resolvedWeComUrl() {
      return (this.kfUrl || (this.chatInfo && this.chatInfo.wx_work_customer_url) || "").trim();
    },
    resolvedChatbotAppId() {
      return ((this.chatInfo && this.chatInfo.appid) || "").trim();
    },
    showServiceButton() {
      if (this.serviceKind === "work_wecom") {
        return this.kfEnabled && !!this.resolvedWeComUrl;
      }
      if (this.serviceKind === "wechat_chatbot") {
        return this.chatbotEnabled && !!this.resolvedChatbotAppId;
      }
      return false;
    },
    serviceText() {
      return this.serviceKind === "wechat_chatbot" ? "咨询" : "客服";
    },
    serviceTitle() {
      return this.serviceKind === "wechat_chatbot" ? "智能咨询" : "企业微信客服";
    },
    serviceButtonClass() {
      return this.serviceKind === "wechat_chatbot"
        ? "floating-tools__btn--chatbot"
        : "floating-tools__btn--kf";
    }
  },
  mounted() {
    this.updateShowTop();
    window.addEventListener("scroll", this.updateShowTop, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.updateShowTop);
  },
  methods: {
    updateShowTop() {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        (document.body && document.body.scrollTop) ||
        0;
      this.showTop = scrollTop >= this.topVisibilityHeight;
    },
    toTop() {
      const top = 0;
      try {
        window.scrollTo({ top, behavior: "smooth" });
      } catch (e) {
        window.scrollTo(0, top);
      }
    },
    async openService() {
      if (!this.showServiceButton || this.opening) return;
      this.opening = true;
      try {
        if (this.serviceKind === "work_wecom") {
          window.open(this.resolvedWeComUrl, "_blank", "noopener,noreferrer");
          return;
        }

        if (this.serviceKind === "wechat_chatbot") {
          let openUrl = "";
          try {
            const { data } = await chatApi.unifiedToken();
            // 兼容后端直接返回 url / 或 token
            if (data && data.url) {
              openUrl = String(data.url).trim();
            } else if (data && data.token) {
              const token = encodeURIComponent(String(data.token));
              const appid = encodeURIComponent(this.resolvedChatbotAppId);
              openUrl = `https://chatbot.weixin.qq.com/webapp/${appid}?token=${token}`;
            }
          } catch (e) {
            // ignore, fallback below
          }

          if (!openUrl) {
            const appid = encodeURIComponent(this.resolvedChatbotAppId);
            openUrl = `https://chatbot.weixin.qq.com/webapp/${appid}`;
          }

          window.open(openUrl, "_blank", "noopener,noreferrer");
        }
      } finally {
        this.opening = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.floating-tools {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.floating-tools__btn {
  width: 88px;
  height: 44px;
  padding: 0 14px 0 10px;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  user-select: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  opacity: 0.96;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}

.floating-tools__btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.22);
  opacity: 1;
}

.floating-tools__btn:active {
  transform: translateY(0);
}

.floating-tools__btn--top {
  background: #f1f1f1;
  color: #333;
}

.floating-tools__btn--kf {
  background: #07c160;
  color: #fff;
}

.floating-tools__btn--chatbot {
  background: #2d8cf0;
  color: #fff;
}

.floating-tools__icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  line-height: 1;
  background: rgba(0, 0, 0, 0.06);
}

.floating-tools__btn--kf .floating-tools__icon {
  background: rgba(255, 255, 255, 0.18);
}

.floating-tools__btn--chatbot .floating-tools__icon {
  background: rgba(255, 255, 255, 0.18);
}

.floating-tools__icon i {
  transform: rotate(-45deg);
  font-size: 16px;
}

.floating-tools__icon-text {
  font-size: 14px;
  font-weight: 700;
}

.floating-tools__text {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .floating-tools {
    right: 10px;
    bottom: 10px;
  }
}
</style>

